export type CollectionId = string;
export type ItemSeed = string;
export type SeedCommitment = string;

export type SizeClass = "small" | "medium" | "large" | "colossal";

export type RarityLabel =
  | "common"
  | "uncommon"
  | "rare"
  | "epic"
  | "legendary"
  | "exotic"
  | "mythical";

export const RARITY_LABELS: readonly RarityLabel[] = [
  "common",
  "uncommon",
  "rare",
  "epic",
  "legendary",
  "exotic",
  "mythical",
];

export type AssetFormat = "glb" | "gltf";

export interface WeightedOption<T> {
  value: T;
  weight: number;
}

export interface NumericRange {
  min: number;
  max: number;
}

export interface CowryGeometryParams {
  shellLength: number;
  shellWidth: number;
  shellHeight: number;
  dorsalBulge: number;
  endTaper: number;
  ventralFlatness: number;
  ventralRecessWidth: number;
  ventralFoldCurve: number;
  longitudinalAsymmetry: number;
}

export interface PatternParams {
  frequency: number;
  warp: number;
  rotation: number;
  contrast: number;
  secondaryScale: number;
}

export interface PbrParams {
  roughness: number;
  metalness: number;
  normalStrength: number;
  clearcoat: number;
  clearcoatRoughness: number;
}

export interface CowryTraits {
  size: SizeClass;
  formFamily: string;
  finishFamily: string;
  patternFamily: string;
  paletteFamily: string;
  ventralStyle: "smooth-fold";
  rarity: RarityLabel;
  geometry: CowryGeometryParams;
  pattern: PatternParams;
  pbr: PbrParams;
}

export type GeometryConstraints = Readonly<Record<keyof CowryGeometryParams, NumericRange>>;

export type PatternAlgorithm = "zebra" | "dots" | "speckles" | "ripples" | "marble" | "mottle" | "iridescent-bloom";

export interface PatternDefinition {
  id: string;
  algorithm: PatternAlgorithm;
}

export interface PaletteDefinition {
  id: string;
  primary: readonly [number, number, number];
  secondary: readonly [number, number, number];
  accent: readonly [number, number, number];
}

export interface FinishDefinition {
  id: string;
  tint: readonly [number, number, number];
  tintStrength: number;
  detailTint: readonly [number, number, number];
  desaturate: number;
  roughness: NumericRange;
  metalness: NumericRange;
}

export interface CollectionConfig {
  /** Immutable internal identifier reserved for deterministic protocol inputs. */
  id: CollectionId;
  /** Public-facing identifier; may change only through an explicit branding migration. */
  publicId: CollectionId;
  name: string;
  version: string;
  itemCount: number;
  geometryProfile: string;
  sizeWeights: readonly WeightedOption<SizeClass>[];
  sizeScale: Readonly<Record<SizeClass, number>>;
  rarityForSize: Readonly<Record<SizeClass, RarityLabel>>;
  formFamilies: readonly WeightedOption<string>[];
  finishFamilies: readonly WeightedOption<string>[];
  patternFamilies: readonly WeightedOption<string>[];
  paletteFamilies: readonly WeightedOption<string>[];
  ventralStyle: CowryTraits["ventralStyle"];
  ventralTreatment: {
    foldEmphasis: number;
    materialEmphasis: boolean;
  };
  lathePropulsion: {
    axisAlignment: "ventral-fold" | "shell-endpoints";
    foldSeating: number;
    diameter: number;
    middleBulge: number;
    chamberDetail: number;
    opacity: number;
    filamentOpacity: number;
    axisOffset: number;
    endpointInset: number;
    profileEasing: number;
    filamentClearance: number;
  };
  finishDefinitions: Readonly<Record<string, FinishDefinition>>;
  patternDefinitions: Readonly<Record<string, PatternDefinition>>;
  paletteDefinitions: Readonly<Record<string, PaletteDefinition>>;
  geometryConstraints: GeometryConstraints;
  textureResolution: number;
  reviewTextureResolution: number;
  defaultAssetFormats: readonly AssetFormat[];
  naming: {
    itemPrefix: string;
    tokenPadding: number;
  };
}
